import type {
  GeneratedDefinition,
  GeneratedSection,
  GeneratedGroupChild,
  GeneratedGroupFaceSection,
  GeneratedStatus,
  GeneratedDimension,
  CustomisationPolicy,
} from "@keydials/api-client";
import { slug } from "./keys";

// ---------------------------------------------------------------------------
// Guided "Create a Key Dial" wizard — shared model helpers, step map and the
// client-side pre-flight that mirrors the server publish gates. The wizard
// produces the SAME GeneratedDefinition the full editor saves, so AI generate /
// create / publish all reuse the existing api-client methods unchanged.
// ---------------------------------------------------------------------------

export type Onramp = "ai" | "example" | "manual";

export type StepId =
  | "welcome"
  | "start"
  | "basics"
  | "describe"
  | "review"
  | "kind"
  | "structure"
  | "palette"
  | "dimensions"
  | "group"
  | "refine"
  | "preview"
  | "check"
  | "finish";

export type PhaseId = "Set up" | "Describe" | "Review" | "Status" | "Shape" | "Refine" | "Launch";

export interface StepMeta {
  phase: PhaseId;
  title: string;
  /** Optional steps can be skipped from the footer. */
  optional?: boolean;
}

export const STEP_META: Record<StepId, StepMeta> = {
  welcome: { phase: "Set up", title: "Welcome" },
  start: { phase: "Set up", title: "Starting point" },
  basics: { phase: "Set up", title: "Name & scope" },
  describe: { phase: "Describe", title: "Describe it" },
  review: { phase: "Review", title: "Review draft" },
  kind: { phase: "Set up", title: "Simple or detailed" },
  structure: { phase: "Shape", title: "Structure" },
  palette: { phase: "Status", title: "Colours & states" },
  // Shape, not Status: since KEY-292 this step is asked AFTER the structure exists and is mostly about
  // which items each axis scores — a question that cannot be answered before there are items to point at.
  dimensions: { phase: "Shape", title: "Item scoring" },
  group: { phase: "Shape", title: "Child dials" },
  refine: { phase: "Refine", title: "Refine", optional: true },
  preview: { phase: "Launch", title: "Preview" },
  check: { phase: "Launch", title: "Check & fix" },
  finish: { phase: "Launch", title: "Save or publish" },
};

export const PHASE_ORDER: PhaseId[] = ["Set up", "Describe", "Review", "Status", "Shape", "Refine", "Launch"];

/** The ordered, branch-aware list of steps the user actually walks. */
export function visibleSteps(model: GeneratedDefinition, onramp: Onramp | null): StepId[] {
  const steps: StepId[] = ["welcome", "start", "basics"];
  if (model.isGroup) {
    steps.push("group");
  } else {
    steps.push("kind");
    if (onramp === "ai") steps.push("describe");
    if (onramp === "ai" || onramp === "example") steps.push("review");
    // Colours first (they are a whole-dial choice), then the structure, then item scoring — which needs
    // the items to already exist. Reversing the old scoring-before-structure order is deliberate (KEY-292):
    // the note driving it is that you work out what to score an item against by looking at the item, and
    // an axis's item scope cannot be picked from an empty dial.
    steps.push("palette");
    steps.push("structure");
    if (model.kind === "Itemised") steps.push("dimensions");
  }
  steps.push("refine", "preview", "check", "finish");
  return steps;
}

// ---------------------------------------------------------------------------
// Status palettes offered as starting points.
// ---------------------------------------------------------------------------

export const RAG_PALETTE: { name: string; statuses: GeneratedStatus[] } = {
  name: "Traffic light (RAG)",
  statuses: [
    { key: "not-started", name: "Not started", colour: "#e11d48", order: 0, allowedRoles: ["Editor", "Reviewer"], isTerminal: false },
    { key: "in-progress", name: "In progress", colour: "#f59e0b", order: 1, allowedRoles: ["Editor", "Reviewer"], isTerminal: false },
    { key: "done", name: "Done", colour: "#16a34a", order: 2, allowedRoles: ["Editor", "Reviewer"], isTerminal: true },
  ],
};

export const WORKFLOW_PALETTE: { name: string; statuses: GeneratedStatus[] } = {
  name: "Workflow",
  statuses: [
    { key: "none", name: "No information", colour: "#c0392b", order: 0, allowedRoles: [], isTerminal: false },
    { key: "review", name: "Pending review", colour: "#2980b9", order: 1, allowedRoles: ["Editor"], isTerminal: false },
    { key: "incomplete", name: "Further info required", colour: "#d68910", order: 2, allowedRoles: ["Editor", "Reviewer"], isTerminal: false },
    { key: "complete", name: "Satisfied", colour: "#229954", order: 3, allowedRoles: ["Reviewer"], isTerminal: true },
  ],
};

export const PRESET_PALETTES = [RAG_PALETTE, WORKFLOW_PALETTE];

function clonePalette(p: { name: string; statuses: GeneratedStatus[] }) {
  return { name: p.name, statuses: p.statuses.map((s) => ({ ...s, allowedRoles: [...s.allowedRoles] })) };
}

// ---------------------------------------------------------------------------
// Key generation — keys are stable internal identifiers; the wizard hides them
// and auto-generates them so a novice never has to think about keys.
// ---------------------------------------------------------------------------

// `slug` has ONE definition, in ../editor/keys.ts — the no-React, unit-tested module the key rules were
// extracted into (keys.test.ts sits beside it). It is re-exported here so every `./support` import site is
// unchanged. This used to be a byte-identical second copy, which mattered because the two were already
// cross-wired: gateAttribute.ts mints node-attribute keys through this export while NodeAttributesEditor
// mints them through editor/keys (via ./shared). Had the copies drifted, the gate shortcut and the editor
// would have produced different keys from the same label.
export { slug };

export function collectKeys(model: GeneratedDefinition): Set<string> {
  const used = new Set<string>();
  for (const sec of model.sections) {
    used.add(sec.key);
    for (const seg of sec.segments) {
      used.add(seg.key);
      for (const it of seg.items) used.add(it.key);
    }
  }
  return used;
}

export function uniqueKey(base: string, used: Set<string>): string {
  const root = base || "key";
  let key = root;
  let n = 2;
  while (used.has(key)) key = `${root}-${n++}`;
  used.add(key);
  return key;
}

// ---------------------------------------------------------------------------
// Group wiring — how a child dial is scaffolded into a group.
//
// One definition of the shape, because there are four surfaces that build it:
// the wizard's group step, the manual create modal, the definition page's Child
// dials tab and the editor's Group tab. They had drifted — the wizard mapped a
// whole child to one slice while the others mapped it per-section — which is
// what made the same children produce two different dials.
// ---------------------------------------------------------------------------

/**
 * The slice of a group draft that carries its children: the overview structure, the mappings, and —
 * on a Mirror-mode group — the face rows composed from those children. Face rows are optional because
 * only the surfaces that can remove a child need to prune them; `undefined` means "not specified", which
 * the API reads as "keep what's persisted" (see GeneratedDefinition.GroupFaceSections).
 */
export type GroupWiring = {
  sections: GeneratedSection[];
  groupChildren: GeneratedGroupChild[];
  groupFaceSections?: GeneratedGroupFaceSection[];
};

/** What scaffolding needs to know about a child: enough of a DefinitionDetail to read its sections. */
export type ScaffoldableChild = { id: string; name: string; sections: { key: string; label: string }[] };

/**
 * Adds one child dial to a group: its own section, carrying a segment per section of that child, each
 * mapped back to the section it mirrors so a slice rolls up just that part of the child.
 *
 * A child with no sections of its own gets a single whole-child slice (`childSectionKey: null`) instead
 * — degraded but valid, where a section with no segments is a group that can't publish. Callers that
 * can't load the child's detail should pass `sections: []` to land here deliberately.
 */
export function withGroupChildScaffolded(wiring: GroupWiring, child: ScaffoldableChild): GroupWiring {
  const usedSection = new Set(wiring.sections.map((s) => s.key));
  const usedSegment = new Set(wiring.sections.flatMap((s) => s.segments.map((sg) => sg.key)));
  let order = wiring.groupChildren.reduce((max, c) => Math.max(max, c.order ?? 0), -1) + 1;

  const sectionKey = uniqueKey(slug(child.name, "child").slice(0, 40), usedSection);
  const added: GeneratedGroupChild[] = [];
  const segments = (child.sections.length
    ? child.sections.map((cs) => ({ suffix: cs.key, label: cs.label, childSectionKey: cs.key as string | null }))
    : [{ suffix: "all", label: child.name, childSectionKey: null }]
  ).map((s) => {
    const key = uniqueKey(`${sectionKey}-${s.suffix}`.slice(0, 70), usedSegment);
    added.push({ childDefinitionId: child.id, overviewSegmentKey: key, childSectionKey: s.childSectionKey, order: order++, weight: 1 });
    return { key, label: s.label, name: s.label, items: [] };
  });

  return {
    sections: [...wiring.sections, { key: sectionKey, label: child.name, segments }],
    groupChildren: [...wiring.groupChildren, ...added],
  };
}

/** The whole group scaffold for an ordered set of children — the creation flows' starting shape. */
export function groupWiringFor(children: ScaffoldableChild[]): GroupWiring {
  return children.reduce(withGroupChildScaffolded, { sections: [], groupChildren: [] });
}

/**
 * Why a dial can't be adopted into a group, or null when it can (KEY-226). A group's children are
 * created and owned by the group — allocating the group allocates each child as an owned subtree node —
 * so a dial that already has live standalone instances would end up with two competing lifecycles. The
 * server refuses to publish such a group; this is the same rule stated up front.
 *
 * One predicate because FOUR surfaces pick children: the definition page's Child dials tab, the editor's
 * Group tab, the manual create modal and the guided wizard. Returning the reason (rather than a boolean)
 * is deliberate — the pickers show the dial greyed WITH the reason, the way draft children are handled,
 * instead of silently omitting a dial the user is looking for.
 *
 * `enabled` is the Features:GroupChildContainment flag: off, nothing is blocked, matching the server.
 */
export function groupChildBlockedReason(
  dial: { standaloneInstanceCount?: number },
  enabled: boolean,
): string | null {
  const n = dial.standaloneInstanceCount ?? 0;
  if (!enabled || n <= 0) {
    return null;
  }
  return `${n} live instance${n === 1 ? "" : "s"} — can't be adopted into a group`;
}

/**
 * The children a group can't be published or sent for checking over because each already has live dials of
 * its own (KEY-550) — {@link groupChildBlockedReason}'s rule read back for a group that is ALREADY wired to
 * such a child: one added before the rule existed, or one that gained a dial since. The pickers stop new
 * ones; this is what warns about the rest before the click. The server's gate stays the authority.
 *
 * A child's checking test dial never counts — the server leaves it out of `standaloneInstanceCount`.
 * `enabled` is Features:GroupChildContainment: off, nothing is returned, matching the server.
 */
export function childrenWithStandaloneInstances<T extends { standaloneInstanceCount?: number }>(
  summaries: readonly T[] | null | undefined,
  enabled: boolean,
): T[] {
  return enabled ? (summaries ?? []).filter((d) => (d.standaloneInstanceCount ?? 0) > 0) : [];
}

/**
 * The tooltip on a group's disabled Publish / Send for checking, or null when neither is blocked. Draft
 * children are named first: publishing them is the fix an author can make without touching anyone's dials.
 */
export function groupPublishBlockedReason(
  draftChildren: readonly { name: string }[] | null | undefined,
  standaloneChildren: readonly { name: string }[],
): string | null {
  if (draftChildren?.length) {
    return `Publish these child dials first: ${draftChildren.map((c) => c.name).join(", ")}`;
  }
  if (standaloneChildren.length) {
    return `These child dials already have live dials of their own, so this group can't include them: ${standaloneChildren.map((c) => c.name).join(", ")}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Fresh drafts.
// ---------------------------------------------------------------------------

export function emptyDraft(name: string, kind: string, palette: { name: string; statuses: GeneratedStatus[] }): GeneratedDefinition {
  return {
    name: name.trim() || "Untitled dial",
    statusSet: clonePalette(palette),
    sections: [],
    kind,
    showCentralProgress: true,
  };
}

export function defaultDimension(statuses: GeneratedStatus[]): GeneratedDimension {
  return {
    key: "status",
    label: "Status",
    order: 0,
    requiredForCompletion: true,
    palette: { name: "Status", statuses: statuses.map((s) => ({ ...s, allowedRoles: [...s.allowedRoles] })) },
  };
}

export function defaultCustomisation(model: GeneratedDefinition): CustomisationPolicy {
  return {
    enabled: true,
    maxSections: Math.max(1, model.sections.length) + 2,
    maxSegmentsPerSection: null,
    maxItemsPerSegment: null,
    allowAddSections: true,
    allowAddSegments: true,
    allowAddItems: true,
    allowEditWording: true,
    allowDisableBaseNodes: true,
    lockedSectionKeys: [],
  };
}

/** A small worked example used by the "Start from an example" on-ramp. */
export function sampleTemplate(): GeneratedDefinition {
  const dim = defaultDimension(WORKFLOW_PALETTE.statuses);
  return {
    name: "Building Control Tracker",
    description: "Tracks the information required to satisfy each Approved Document part on a building-control project.",
    category: "Compliance",
    industry: "Construction",
    kind: "Itemised",
    showCentralProgress: true,
    statusSet: clonePalette(WORKFLOW_PALETTE),
    dimensions: [dim],
    sections: [
      {
        key: "part-b",
        label: "B",
        description: "Fire safety",
        segments: [
          {
            key: "b1",
            label: "B1",
            name: "Means of warning & escape",
            items: [
              { key: "b1-fire-strategy", name: "Fire strategy report" },
              { key: "b1-escape-drawings", name: "Escape-route drawings" },
            ],
          },
          {
            key: "b2",
            label: "B2",
            name: "Internal fire spread (linings)",
            items: [{ key: "b2-lining-spec", name: "Wall & ceiling lining specification" }],
          },
        ],
      },
      {
        key: "part-m",
        label: "M",
        description: "Access to and use of buildings",
        segments: [
          {
            key: "m1",
            label: "M1",
            name: "Access & use",
            items: [{ key: "m1-access-statement", name: "Access statement" }],
          },
        ],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Pre-flight — mirrors the server publish gates (DefinitionService.cs) so the
// user fixes problems here instead of meeting a raw "Cannot publish — …" toast.
// ---------------------------------------------------------------------------

export interface Check {
  ok: boolean;
  label: string;
  detail?: string;
  fixStep?: StepId;
}

/**
 * What the gates can't read off the model alone. A group's children may be drafts while it's built, but
 * the group can't publish until each one has a published version — a fact that lives on the catalogue
 * list, not on the definition being edited (KEY-197). So does whether a child already has live dials of its
 * own (KEY-550).
 */
export interface PreflightContext {
  draftChildNames?: string[];
  /** Chosen children with live dials of their own. Leave undefined while Features:GroupChildContainment is
   * off: the check is only listed when the server actually enforces it. */
  standaloneChildNames?: string[];
}

export function preflight(model: GeneratedDefinition, ctx: PreflightContext = {}): Check[] {
  const checks: Check[] = [];
  const itemised = model.kind === "Itemised" && !model.isGroup;
  const allowAddItems = model.customisation?.enabled && model.customisation.allowAddItems;

  // 1 — at least one section.
  checks.push({
    ok: model.sections.length > 0,
    label: "Has at least one section",
    detail: model.sections.length > 0 ? undefined : "Add a section to continue.",
    fixStep: model.isGroup ? "group" : "structure",
  });

  if (model.isGroup) {
    const mapped = model.groupChildren?.length ?? 0;
    checks.push({
      ok: mapped > 0,
      label: "At least one child dial is mapped",
      detail: mapped > 0 ? undefined : "Pick at least one child dial.",
      fixStep: "group",
    });
    const drafts = ctx.draftChildNames ?? [];
    checks.push({
      ok: drafts.length === 0,
      label: "Every child dial is published",
      detail: drafts.length === 0
        ? undefined
        : `A group rolls up its children’s published versions, so publish ${drafts.join(", ")} first (the group can stay a draft meanwhile).`,
      fixStep: "group",
    });
    if (ctx.standaloneChildNames) {
      const standalone = ctx.standaloneChildNames;
      checks.push({
        ok: standalone.length === 0,
        label: "No child dial has live dials of its own",
        detail: standalone.length === 0
          ? undefined
          : `A group creates and owns its children’s dials, so it can’t include ${standalone.join(", ")} while they have their own. Take them out of the group, or archive their dials first.`,
        fixStep: "group",
      });
    }
    checks.push({
      ok: !!model.rollupStrategy,
      label: "A rollup strategy is chosen",
      detail: model.rollupStrategy ? undefined : "Pick how a slice's colour is decided.",
      fixStep: "group",
    });
  } else {
    // 2 — every section has a segment.
    const emptySection = model.sections.find((s) => s.segments.length === 0);
    checks.push({
      ok: !emptySection,
      label: "Every section has at least one segment",
      detail: emptySection ? `Section “${emptySection.label || "untitled"}” has no segments yet.` : undefined,
      fixStep: "structure",
    });

    // 3 — every segment has an item (itemised, unless instances may add items).
    if (itemised && !allowAddItems) {
      let emptySeg: { label: string; name: string } | undefined;
      for (const sec of model.sections) {
        const seg = sec.segments.find((sg) => sg.items.length === 0);
        if (seg) {
          emptySeg = { label: seg.label, name: seg.name };
          break;
        }
      }
      checks.push({
        ok: !emptySeg,
        label: "Every segment lists at least one item",
        detail: emptySeg ? `Segment “${emptySeg.name || emptySeg.label || "untitled"}” has no items yet.` : undefined,
        fixStep: "structure",
      });
    }
  }

  // 4 — palette has a status.
  checks.push({
    ok: model.statusSet.statuses.length > 0,
    label: "Your colours include at least one state",
    detail: model.statusSet.statuses.length > 0 ? undefined : "Add at least one status.",
    fixStep: "palette",
  });

  // 5 — customisation cap fits.
  if (model.customisation?.enabled) {
    const fits = model.customisation.maxSections >= model.sections.length;
    checks.push({
      ok: fits,
      label: "Tailoring limit fits your number of sections",
      detail: fits ? undefined : `Max sections (${model.customisation.maxSections}) is below your ${model.sections.length} sections — raise it.`,
      fixStep: "refine",
    });
  }

  return checks;
}

export function allClear(model: GeneratedDefinition, ctx: PreflightContext = {}): boolean {
  return preflight(model, ctx).every((c) => c.ok);
}

/** The structure-step subset of the gates, for the live readiness pill in the rail. */
export function structureReadiness(model: GeneratedDefinition, ctx: PreflightContext = {}): Check[] {
  return preflight(model, ctx).filter((c) => c.fixStep === "structure" || c.fixStep === "palette");
}
